import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Trash2, Save, Upload, ArrowLeft, RefreshCw, UserPlus, List } from "lucide-react";
import { toast } from "sonner";
import ImageCropper from "@/components/ImageCropper";
import { CustomerSelect } from "@/components/CustomerSelect";
import { CATEGORIES, buildSpecsForCategory } from "@/lib/specTemplates";
import { CURRENCIES, convertFromAED, formatMoney } from "@/lib/currencies";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Merge new template spec labels with any existing user-typed values (keyed by label).
const mergeSpecs = (existing, fromTemplate) => {
  const map = new Map((existing || []).filter((s) => s.label).map((s) => [s.label.toLowerCase(), s.value]));
  return fromTemplate.map((s) => ({ label: s.label, value: map.get(s.label.toLowerCase()) || "" }));
};

const genOptionId = () =>
  (globalThis.crypto?.randomUUID?.() || `opt_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`);

const blankItem = (optionId) => ({ description: "", image: "", images: [], quantity: 1, unit_price: 0, category: "", specs: [], option_id: optionId || null });

const defaultQuote = () => {
  const firstOptId = genOptionId();
  return {
    quote_ref: "",
    quote_date: todayISO(),
    customer_name: "",
    customer_place: "",
    customer_details: "",
    customer_contact: "",
    kind_attention: "",
    subject: "",
    welcome_message:
      "Thank you for your enquiry. We are pleased to submit our best price for your kind consideration as detailed below.",
    closing_message:
      "We hope our offer meets your requirements and look forward to receiving your valued order at the earliest.",
    options: [{ id: firstOptId, name: "Option 1", description: "" }],
    line_items: [blankItem(firstOptId)],
    payment_terms: "Cash on Delivery / Bank Transfer",
    lead_time: "Ex-Works – Dubai",
    valid_until: "2 weeks from quote date",
    vat_mode: "exclude",
    vat_percent: 5,
    items_vat_exempt: false,
    currency: "AED",
    fx_rate: 1,
    status: "draft",
  };
};

const QuoteFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(defaultQuote());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cropState, setCropState] = useState({ open: false, src: "", idx: -1 });
  const [products, setProducts] = useState([]);
  const [fxLoading, setFxLoading] = useState(false);
  const [customerRefreshKey, setCustomerRefreshKey] = useState(0);
  const [savingCustomer, setSavingCustomer] = useState(false);

  const applySavedCustomer = (c) => {
    setForm((f) => ({
      ...f,
      customer_name: c.name || "",
      customer_place: c.place || "",
      kind_attention: c.kind_attention || "",
      customer_contact: c.contact || "",
      customer_details: c.details || "",
    }));
    toast.success(`Loaded customer: ${c.name}`);
  };

  const saveCustomer = async () => {
    if (!form.customer_name.trim()) return toast.error("Enter a customer name first");
    setSavingCustomer(true);
    try {
      await apiClient.post("/customers", {
        name: form.customer_name,
        place: form.customer_place,
        kind_attention: form.kind_attention,
        contact: form.customer_contact,
        details: form.customer_details,
      });
      toast.success("Customer saved to directory");
      setCustomerRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save customer");
    }
    setSavingCustomer(false);
  };

  useEffect(() => {
    apiClient.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const fetchFxRate = async (currencyCode) => {
    if (!currencyCode || currencyCode === "AED") {
      setForm((f) => ({ ...f, fx_rate: 1 }));
      return;
    }
    setFxLoading(true);
    try {
      const { data } = await apiClient.get("/fx-rates");
      const rate = data?.rates?.[currencyCode];
      if (typeof rate === "number" && rate > 0) {
        setForm((f) => ({ ...f, fx_rate: rate }));
        if (data.source === "fallback") toast.warning("Using offline FX rate — network unavailable");
      } else {
        toast.error(`No FX rate found for ${currencyCode}`);
      }
    } catch {
      toast.error("Failed to fetch exchange rate");
    } finally {
      setFxLoading(false);
    }
  };

  const changeCurrency = (code) => {
    setForm((f) => ({ ...f, currency: code, fx_rate: code === "AED" ? 1 : f.fx_rate }));
    fetchFxRate(code);
  };

  // O(1) product-by-model lookup + memoized <datalist> options.
  // The datalist is rendered ONCE at the top of the form and shared by every line item.
  const productByModelIndex = useMemo(() => {
    const m = new Map();
    for (const p of products) {
      const key = (p.model_number || "").trim().toLowerCase();
      if (key) m.set(key, p);
    }
    return m;
  }, [products]);
  const productDatalistOptions = useMemo(
    () => products.map((p) => (
      <option key={p.id} value={p.model_number}>{p.name || p.category}</option>
    )),
    [products]
  );
  const productByModel = useCallback((mn) => {
    const t = (mn || "").trim().toLowerCase();
    if (!t) return null;
    return productByModelIndex.get(t) || null;
  }, [productByModelIndex]);

  const applyProductToItem = useCallback((idx, product) => {
    setForm((f) => {
      const items = [...f.line_items];
      const newSpecs = (product.specs || []).map((s) => ({ ...s }));
      // Inject Model row if not present
      const hasModel = newSpecs.some((s) => s.label.toLowerCase() === "model");
      if (!hasModel) newSpecs.unshift({ label: "Model", value: product.model_number });

      // Copy product images into the line item (only if line item has no image yet)
      const cur = items[idx];
      const curImgs = (cur.images && cur.images.length > 0)
        ? cur.images
        : (cur.image ? [cur.image] : []);
      const nextImgs = (product.images && product.images.length > 0 && curImgs.length === 0)
        ? [...product.images]
        : curImgs;

      items[idx] = {
        ...cur,
        category: product.category || cur.category,
        specs: newSpecs,
        image: "",
        images: nextImgs,
      };
      return { ...f, line_items: items };
    });
    toast.success(`Applied data sheet: ${product.model_number}`);
  }, []);

  const handleModelChange = useCallback((idx, mn) => {
    setForm((f) => {
      const items = [...f.line_items];
      const specs = [...(items[idx].specs || [])];
      const mIdx = specs.findIndex((s) => s.label.toLowerCase() === "model");
      if (mIdx >= 0) specs[mIdx] = { ...specs[mIdx], value: mn };
      else specs.unshift({ label: "Model", value: mn });
      items[idx] = { ...items[idx], specs };
      return { ...f, line_items: items };
    });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    apiClient.get(`/quotes/${id}`).then(({ data }) => {
      // Migrate legacy single-image to images[] for back-compat
      const items = (data.line_items || []).map((li) => ({
        ...li,
        images: (li.images && li.images.length > 0)
          ? li.images
          : (li.image ? [li.image] : []),
        option_id: li.option_id || null,
      }));

      // Backfill options for quotes saved before the Options feature existed
      let opts = data.options && data.options.length > 0 ? data.options : [];
      if (opts.length === 0) {
        const fallbackId = genOptionId();
        opts = [{ id: fallbackId, name: "Option 1", description: "" }];
        for (const li of items) if (!li.option_id) li.option_id = fallbackId;
      } else {
        const validIds = new Set(opts.map((o) => o.id));
        const fallbackId = opts[0].id;
        for (const li of items) if (!validIds.has(li.option_id)) li.option_id = fallbackId;
      }

      setForm({ ...defaultQuote(), ...data, options: opts, line_items: items });
      setLoading(false);
    }).catch(() => {
      toast.error("Failed to load quote");
      nav("/");
    });
  }, [id, isEdit, nav]);

  // Auto-prefill the next reference number on new quote creation
  useEffect(() => {
    if (isEdit) return;
    apiClient.get("/quotes/next-ref").then(({ data }) => {
      setForm((f) => (f.quote_ref ? f : { ...f, quote_ref: data.quote_ref }));
    }).catch(() => {});
  }, [isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = useCallback((idx, key, value) => {
    setForm((f) => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [key]: value };
      return { ...f, line_items: items };
    });
  }, []);

  const addItem = (optionId) =>
    setForm((f) => {
      const targetOpt = optionId || (f.options?.[0]?.id) || null;
      return { ...f, line_items: [...f.line_items, blankItem(targetOpt)] };
    });
  const removeItem = useCallback((idx) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) })), []);

  const addOption = () =>
    setForm((f) => {
      const nextNum = (f.options?.length || 0) + 1;
      const newId = genOptionId();
      const newOpt = { id: newId, name: `Option ${nextNum}`, description: "" };
      return {
        ...f,
        options: [...(f.options || []), newOpt],
        line_items: [...f.line_items, blankItem(newId)],
      };
    });

  const renameOption = (id, name) =>
    setForm((f) => ({
      ...f,
      options: (f.options || []).map((o) => (o.id === id ? { ...o, name } : o)),
    }));

  const removeOption = (id) =>
    setForm((f) => {
      const remaining = (f.options || []).filter((o) => o.id !== id);
      if (remaining.length === 0) {
        toast.error("At least one option is required");
        return f;
      }
      // Move any orphan items to the first remaining option so they aren't silently lost
      const fallback = remaining[0].id;
      const newItems = f.line_items
        .filter((li) => li.option_id !== id)
        .concat(
          f.line_items
            .filter((li) => li.option_id === id)
            .map(() => null) // discard items belonging to removed option
        )
        .filter(Boolean);
      // Re-map any items with option_id not in remaining -> fallback
      const validIds = new Set(remaining.map((o) => o.id));
      const finalItems = newItems.map((li) =>
        validIds.has(li.option_id) ? li : { ...li, option_id: fallback }
      );
      return { ...f, options: remaining, line_items: finalItems };
    });

  const setItemSpec = useCallback((itemIdx, specIdx, key, value) => {
    setForm((f) => {
      const items = [...f.line_items];
      const specs = [...(items[itemIdx].specs || [])];
      specs[specIdx] = { ...specs[specIdx], [key]: value };
      items[itemIdx] = { ...items[itemIdx], specs };
      return { ...f, line_items: items };
    });
  }, []);
  const addItemSpec = useCallback((itemIdx) => {
    setForm((f) => {
      const items = [...f.line_items];
      const specs = [...(items[itemIdx].specs || []), { label: "", value: "" }];
      items[itemIdx] = { ...items[itemIdx], specs };
      return { ...f, line_items: items };
    });
  }, []);
  const removeItemSpec = useCallback((itemIdx, specIdx) => {
    setForm((f) => {
      const items = [...f.line_items];
      items[itemIdx] = {
        ...items[itemIdx],
        specs: (items[itemIdx].specs || []).filter((_, i) => i !== specIdx),
      };
      return { ...f, line_items: items };
    });
  }, []);
  const applyCategory = useCallback((itemIdx, category) => {
    setForm((f) => {
      const items = [...f.line_items];
      const existing = items[itemIdx].specs || [];
      const hasValues = existing.some((s) => s.value && s.value.trim());
      const newSpecs = buildSpecsForCategory(category);
      // If user already entered values, ask via confirm; for simplicity replace only if empty.
      items[itemIdx] = {
        ...items[itemIdx],
        category,
        specs: hasValues && newSpecs.length === 0 ? existing : newSpecs.length ? mergeSpecs(existing, newSpecs) : existing,
      };
      return { ...f, line_items: items };
    });
  }, []);

  const handleImage = useCallback((idx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setCropState({ open: true, src: e.target.result, idx });
    reader.readAsDataURL(file);
  }, []);

  const handleCropSave = (dataUrl) => {
    if (cropState.idx >= 0) {
      // Append to images[]
      setForm((f) => {
        const items = [...f.line_items];
        const cur = items[cropState.idx];
        const existing = cur.images && cur.images.length > 0
          ? cur.images
          : (cur.image ? [cur.image] : []);
        items[cropState.idx] = { ...cur, image: "", images: [...existing, dataUrl] };
        return { ...f, line_items: items };
      });
    }
    setCropState({ open: false, src: "", idx: -1 });
  };
  const handleCropCancel = () => setCropState({ open: false, src: "", idx: -1 });

  const removeItemImage = useCallback((itemIdx, imgIdx) => {
    setForm((f) => {
      const items = [...f.line_items];
      const cur = items[itemIdx];
      const list = cur.images && cur.images.length > 0
        ? cur.images
        : (cur.image ? [cur.image] : []);
      const next = list.filter((_, i) => i !== imgIdx);
      items[itemIdx] = { ...cur, image: "", images: next };
      return { ...f, line_items: items };
    });
  }, []);

  const subTotal = form.line_items.reduce(
    (s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0),
    0
  );
  const chargesTaxable = (form.additional_charges || [])
    .filter((c) => !c.vat_exempt)
    .reduce((s, c) => s + Number(c.amount || 0), 0);
  const chargesExempt = (form.additional_charges || [])
    .filter((c) => c.vat_exempt)
    .reduce((s, c) => s + Number(c.amount || 0), 0);
  const itemsTaxable = form.items_vat_exempt ? 0 : subTotal;
  const taxableBase = itemsTaxable + chargesTaxable;
  const vatAmount = form.vat_mode === "exclude" ? taxableBase * (Number(form.vat_percent) / 100) : 0;
  const grandTotalAED =
    subTotal + chargesTaxable + chargesExempt + vatAmount;

  // Currency conversion helpers (prices are stored in AED, displayed in target currency)
  const fxRate = form.currency === "AED" ? 1 : Number(form.fx_rate) || 0;
  const showConversion = form.currency !== "AED" && fxRate > 0;
  const conv = (aed) => convertFromAED(aed, fxRate, form.currency);

  const addCharge = (label = "") =>
    setForm((f) => ({ ...f, additional_charges: [...(f.additional_charges || []), { label, amount: 0, vat_exempt: false }] }));
  const setCharge = (i, key, v) =>
    setForm((f) => {
      const list = [...(f.additional_charges || [])];
      list[i] = { ...list[i], [key]: v };
      return { ...f, additional_charges: list };
    });
  const removeCharge = (i) =>
    setForm((f) => ({ ...f, additional_charges: (f.additional_charges || []).filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.quote_ref.trim()) return toast.error("Quote Reference is required");
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.subject.trim()) return toast.error("Subject is required");
    if (form.line_items.length === 0) return toast.error("Add at least one line item");

    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put(`/quotes/${id}`, form);
        toast.success("Quote updated");
        nav(`/quotes/${id}`);
      } else {
        const { data } = await apiClient.post(`/quotes`, form);
        toast.success("Quote created");
        nav(`/quotes/${data.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6 pb-20" data-testid="quote-form-page">
      {/* Shared products datalist — rendered once for all line-item Model inputs (perf) */}
      <datalist id="quote-products-datalist">{productDatalistOptions}</datalist>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="rounded-sm" data-testid="back-button">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold ep-text-primary">
              {isEdit ? "Edit Quote" : "New Quote"}
            </h1>
            <p className="text-sm text-slate-500">Fill in all sections, then save.</p>
          </div>
        </div>
        <Button className="rounded-sm ep-amber hover:ep-amber" onClick={submit} disabled={saving} data-testid="save-quote-button">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Saving…" : isEdit ? "Update Quote" : "Save Quote"}
        </Button>
      </div>

      {/* Section 1: Quote header */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <h2 className="font-heading mb-4 text-sm font-bold uppercase tracking-wider text-slate-500">1 • Quote Details</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Quote Reference *">
            <div className="flex gap-1">
              <Input value={form.quote_ref} onChange={(e) => set("quote_ref", e.target.value)} placeholder="e.g. M1-6000" className="rounded-sm" data-testid="quote-ref-input" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm whitespace-nowrap"
                onClick={async () => {
                  try {
                    const { data } = await apiClient.get("/quotes/next-ref");
                    set("quote_ref", data.quote_ref);
                    toast.success(`Next reference: ${data.quote_ref}`);
                  } catch {
                    toast.error("Failed to fetch next reference");
                  }
                }}
                data-testid="auto-ref-button"
              >
                Auto
              </Button>
            </div>
          </Field>
          <Field label="Quote Date *">
            <Input type="date" value={form.quote_date} onChange={(e) => set("quote_date", e.target.value)} className="rounded-sm" data-testid="quote-date-input" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="rounded-sm" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Card>

      {/* Section 2: Customer */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">2 • Customer Information</h2>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-72" data-testid="customer-picker">
              <CustomerSelect
                value={form.customer_name}
                onSelect={applySavedCustomer}
                refreshKey={customerRefreshKey}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm whitespace-nowrap"
              onClick={saveCustomer}
              disabled={savingCustomer}
              data-testid="save-customer-button"
            >
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
              {savingCustomer ? "Saving…" : "Save Customer"}
            </Button>
          </div>
        </div>
        <div className="mb-4 rounded-sm border border-dashed border-[#1E40AF]/30 bg-blue-50/40 px-3 py-2 text-[11px] text-slate-600" data-testid="customer-directory-note">
          Pick a saved customer to auto-fill the fields below, or fill them in and click <span className="font-semibold">Save Customer</span>. Customers are also saved automatically whenever you save a quote.
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Customer Name *">
            <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Company name" className="rounded-sm" data-testid="customer-name-input" />
          </Field>
          <Field label="Place / City">
            <Input value={form.customer_place} onChange={(e) => set("customer_place", e.target.value)} placeholder="Dubai, UAE" className="rounded-sm" data-testid="customer-place-input" />
          </Field>
          <Field label="Kind Attention">
            <Input value={form.kind_attention} onChange={(e) => set("kind_attention", e.target.value)} placeholder="Mr. Paul George" className="rounded-sm" data-testid="kind-attention-input" />
          </Field>
          <Field label="Contact Number">
            <Input value={form.customer_contact} onChange={(e) => set("customer_contact", e.target.value)} placeholder="+971-50-..." className="rounded-sm" data-testid="customer-contact-input" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Customer Address / Details">
              <Textarea value={form.customer_details} onChange={(e) => set("customer_details", e.target.value)} rows={2} placeholder="Full address, PO Box, country…" className="rounded-sm" data-testid="customer-details-input" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Subject *">
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Quotation for Heavy Duty Plastic Pallet" className="rounded-sm" data-testid="subject-input" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Welcome Message">
              <Textarea value={form.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} rows={2} className="rounded-sm" data-testid="welcome-message-input" />
            </Field>
          </div>
        </div>
      </Card>

      {/* Section 3: Line items grouped by Option */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">3 • Products / Line Items</h2>
            <div className="mt-1 text-[11px] text-slate-500">Group items into Options (e.g. Standard / Premium) so customers can easily compare.</div>
          </div>
          <Button variant="default" size="sm" className="rounded-sm ep-primary hover:ep-primary text-white" onClick={addOption} data-testid="add-option-button">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Option
          </Button>
        </div>
        <div className="space-y-6">
          {(form.options || []).map((opt, optIdx) => {
            const optItems = form.line_items
              .map((li, absIdx) => ({ li, absIdx }))
              .filter(({ li }) => li.option_id === opt.id);
            const optSubtotal = optItems.reduce(
              (s, { li }) => s + Number(li.quantity || 0) * Number(li.unit_price || 0),
              0
            );
            return (
              <div key={opt.id} className="rounded-sm border-2 border-[#1E40AF]/20 bg-slate-50/40 p-4" data-testid={`option-group-${optIdx}`}>
                <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-1 items-center gap-2">
                    <div className="rounded-sm bg-[#1E40AF] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      Opt {optIdx + 1}
                    </div>
                    <Input
                      value={opt.name}
                      onChange={(e) => renameOption(opt.id, e.target.value)}
                      className="max-w-xs rounded-sm border-transparent bg-transparent text-sm font-bold uppercase tracking-wider text-[#1E40AF] hover:border-slate-300"
                      data-testid={`option-name-${optIdx}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => addItem(opt.id)} data-testid={`add-line-item-button-${optIdx}`}>
                      <Plus className="mr-1 h-3.5 w-3.5" /> Add Item
                    </Button>
                    {(form.options || []).length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-sm text-red-600 hover:text-red-700"
                        onClick={() => {
                          if (window.confirm(`Remove ${opt.name} and all its items?`)) removeOption(opt.id);
                        }}
                        data-testid={`remove-option-${optIdx}`}
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove Option
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  {optItems.length === 0 && (
                    <div className="rounded-sm border border-dashed border-[#E5E7EB] bg-white p-3 text-center text-xs text-slate-400">
                      No items in this option yet. Click <span className="font-semibold">Add Item</span> to add one.
                    </div>
                  )}
                  {optItems.map(({ li, absIdx: idx }) => (
                    <LineItemCard
                      key={idx}
                      li={li}
                      idx={idx}
                      setItem={setItem}
                      removeItem={removeItem}
                      handleImage={handleImage}
                      removeItemImage={removeItemImage}
                      handleModelChange={handleModelChange}
                      productByModel={productByModel}
                      applyProductToItem={applyProductToItem}
                      applyCategory={applyCategory}
                      addItemSpec={addItemSpec}
                      setItemSpec={setItemSpec}
                      removeItemSpec={removeItemSpec}
                    />
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between rounded-sm border-t border-[#1E40AF]/30 bg-white/60 px-3 py-2 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    {opt.name} — Subtotal
                  </span>
                  <span className="tabular-nums font-bold text-[#1E40AF]" data-testid={`option-subtotal-${optIdx}`}>
                    {optSubtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} AED
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {(form.options || []).length > 1 ? (
          <div className="mt-6 ml-auto w-full max-w-sm rounded-sm border border-dashed border-[#1E40AF]/40 bg-blue-50/50 p-3 text-[11px] text-slate-600" data-testid="multi-option-note">
            Multiple options are active — each option shows its own subtotal above and remains independent. No combined subtotal or grand total is calculated.
          </div>
        ) : (
          <div className="mt-6 ml-auto w-full max-w-sm space-y-2 rounded-sm border border-[#E5E7EB] bg-slate-50 p-4 text-sm">
            <Row label={`Subtotal (Items)${form.items_vat_exempt ? " — VAT-free" : ""}`} value={subTotal} testId="subtotal" />
            {(form.additional_charges || []).map((c, i) => (
              <Row key={i} label={`${c.label || "Charge"}${c.vat_exempt ? " (VAT-free)" : ""}`} value={c.amount} testId={`charges-preview-${i}`} />
            ))}
            <Row label={taxableBase === 0 ? "VAT Free (0%)" : `VAT (${form.vat_percent}%) — ${form.vat_mode === "exclude" ? "added" : "included"}`} value={vatAmount} testId="vat-amount" />
            <div className="border-t border-[#E5E7EB] pt-2">
              <Row label="Grand Total (AED)" value={grandTotalAED} bold testId="grand-total" />
            </div>
            {showConversion && (
              <div className="mt-1 rounded-sm bg-white/70 p-2 text-[11px] text-slate-600" data-testid="currency-conversion-preview">
                <div className="flex items-center justify-between">
                  <span className="uppercase tracking-wider">Grand Total ({form.currency})</span>
                  <span className="font-bold text-[#1E40AF] tabular-nums">
                    {formatMoney(conv(grandTotalAED), form.currency)}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] text-slate-400">
                  Rate: 1 AED = {Number(fxRate).toFixed(6)} {form.currency}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Additional Charges (VAT applies) */}
        <div className="mt-6 rounded-sm border border-[#E5E7EB] bg-slate-50/60 p-4">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Additional Charges</div>
              <div className="mt-1 text-[10px] text-slate-500">Transportation, freight, delivery, etc. Tick <span className="font-semibold">VAT Exempt</span> to exclude a charge from VAT calculation.</div>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => addCharge("Transportation Charge")} data-testid="add-transport-btn">
                <Plus className="mr-1 h-3 w-3" /> Transportation
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => addCharge("Freight Charge")} data-testid="add-freight-btn">
                <Plus className="mr-1 h-3 w-3" /> Freight
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => addCharge("Delivery Charge")} data-testid="add-delivery-btn">
                <Plus className="mr-1 h-3 w-3" /> Delivery
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => addCharge("")} data-testid="add-custom-charge-btn">
                <Plus className="mr-1 h-3 w-3" /> Custom
              </Button>
            </div>
          </div>
          {(form.additional_charges || []).length === 0 ? (
            <div className="rounded-sm border border-dashed border-[#E5E7EB] bg-white p-3 text-center text-xs text-slate-400">
              No additional charges. Click a button above to add one.
            </div>
          ) : (
            <div className="space-y-2">
              {(form.additional_charges || []).map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`charge-row-${i}`}>
                  <div className="col-span-12 md:col-span-5">
                    <Input
                      value={c.label}
                      onChange={(e) => setCharge(i, "label", e.target.value)}
                      placeholder="Charge name (e.g. Transportation)"
                      className="rounded-sm bg-white"
                      data-testid={`charge-label-${i}`}
                    />
                  </div>
                  <div className="col-span-8 md:col-span-3">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={c.amount}
                      onChange={(e) => setCharge(i, "amount", e.target.value)}
                      placeholder="Amount (AED)"
                      className="rounded-sm bg-white tabular-nums"
                      data-testid={`charge-amount-${i}`}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-3 flex items-center">
                    <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 w-full h-9">
                      <input
                        type="checkbox"
                        checked={!!c.vat_exempt}
                        onChange={(e) => setCharge(i, "vat_exempt", e.target.checked)}
                        className="h-3.5 w-3.5 accent-slate-900"
                        data-testid={`charge-vat-exempt-${i}`}
                      />
                      <span className="whitespace-nowrap">VAT Exempt</span>
                    </label>
                  </div>
                  <div className="col-span-1 flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-sm text-red-600 hover:text-red-700"
                      onClick={() => removeCharge(i)}
                      data-testid={`remove-charge-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Section 4: Terms */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <h2 className="font-heading mb-4 text-sm font-bold uppercase tracking-wider text-slate-500">4 • Terms & Conditions</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Payment Terms">
            <Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} className="rounded-sm" data-testid="payment-terms-input" />
          </Field>
          <Field label="Delivery / Lead Time">
            <Input value={form.lead_time} onChange={(e) => set("lead_time", e.target.value)} className="rounded-sm" data-testid="lead-time-input" />
            <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-500">
              <span>Editable — change anytime.</span>
              <button
                type="button"
                onClick={() => set("lead_time", "Ex-Works – Dubai")}
                className="rounded-sm border border-[#E5E7EB] bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
                data-testid="lead-time-reset-btn"
              >
                Use &ldquo;Ex-Works – Dubai&rdquo;
              </button>
            </div>
          </Field>
          <Field label="Quote Valid Up To">
            <Input value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className="rounded-sm" data-testid="valid-until-input" />
          </Field>
          <Field label="VAT Treatment">
            <Select value={form.vat_mode} onValueChange={(v) => set("vat_mode", v)}>
              <SelectTrigger className="rounded-sm" data-testid="vat-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="exclude">VAT Exclusive (added on top)</SelectItem>
                <SelectItem value="include">VAT Inclusive</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Currency">
            <div className="flex items-center gap-2">
              <Select value={form.currency} onValueChange={changeCurrency}>
                <SelectTrigger className="rounded-sm" data-testid="currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.currency !== "AED" && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-sm"
                  onClick={() => fetchFxRate(form.currency)}
                  disabled={fxLoading}
                  title="Refresh live bank rate"
                  data-testid="refresh-fx-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${fxLoading ? "animate-spin" : ""}`} />
                </Button>
              )}
            </div>
            {form.currency !== "AED" && (
              <div className="mt-1 text-[10px] text-slate-500" data-testid="fx-rate-info">
                Live bank rate: 1 AED = <span className="font-semibold text-slate-700">{Number(form.fx_rate || 0).toFixed(6)} {form.currency}</span>
              </div>
            )}
          </Field>
          <div className="md:col-span-2">
            <label
              className="flex cursor-pointer items-center gap-3 rounded-sm border border-[#E5E7EB] bg-slate-50 px-3 py-2 text-sm hover:bg-slate-100"
              data-testid="items-vat-exempt-toggle-wrapper"
            >
              <input
                type="checkbox"
                checked={!!form.items_vat_exempt}
                onChange={(e) => set("items_vat_exempt", e.target.checked)}
                className="h-4 w-4 accent-slate-900"
                data-testid="items-vat-exempt-toggle"
              />
              <div>
                <div className="font-semibold text-slate-700">Items Subtotal is VAT-Exempt</div>
                <div className="text-[11px] text-slate-500">When enabled, no VAT is charged on the items subtotal. VAT still applies to non-exempt additional charges.</div>
              </div>
            </label>
          </div>
          <div className="md:col-span-2">
            <Field label="Closing Message">
              <Textarea value={form.closing_message} onChange={(e) => set("closing_message", e.target.value)} rows={2} className="rounded-sm" data-testid="closing-message-input" />
            </Field>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button className="rounded-sm ep-amber hover:ep-amber" onClick={submit} disabled={saving} data-testid="save-quote-bottom">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Saving…" : isEdit ? "Update Quote" : "Save Quote"}
        </Button>
      </div>

      <ImageCropper
        open={cropState.open}
        src={cropState.src}
        onSave={handleCropSave}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

// Memoized single line-item card. Re-renders ONLY when its own `li`/`idx`
// change (all handler props are stable via useCallback in the parent), so
// typing in one item no longer re-renders the whole form.
const LineItemCard = React.memo(function LineItemCard({
  li,
  idx,
  setItem,
  removeItem,
  handleImage,
  removeItemImage,
  handleModelChange,
  productByModel,
  applyProductToItem,
  applyCategory,
  addItemSpec,
  setItemSpec,
  removeItemSpec,
}) {
  const lineTotal = Number(li.quantity || 0) * Number(li.unit_price || 0);
  const imgs = (li.images && li.images.length > 0) ? li.images : (li.image ? [li.image] : []);
  const modelValue = (li.specs || []).find((s) => s.label.toLowerCase() === "model")?.value || "";
  return (
    <div className="grid grid-cols-12 gap-3 rounded-sm border border-[#E5E7EB] p-4" data-testid={`line-item-${idx}`}>
      <div className="col-span-12 md:col-span-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          Images {(imgs.length > 0) && (
            <span className="ml-1 text-slate-400">({imgs.length})</span>
          )}
        </Label>
        <div className="mt-1 space-y-1">
          {imgs.map((src, imgIdx) => (
            <div key={imgIdx} className="relative h-20 overflow-hidden rounded-sm border border-[#E5E7EB] bg-white" data-testid={`item-${idx}-img-${imgIdx}`}>
              <img src={src} alt="" className="h-full w-full object-contain" />
              <button
                type="button"
                onClick={() => removeItemImage(idx, imgIdx)}
                className="absolute right-0.5 top-0.5 rounded-sm bg-white/90 px-1 text-[10px] text-red-600 hover:bg-white"
                data-testid={`item-${idx}-img-remove-${imgIdx}`}
              >
                ✕
              </button>
            </div>
          ))}
          <label className="flex h-16 cursor-pointer items-center justify-center rounded-sm border border-dashed border-[#E5E7EB] bg-slate-50 hover:bg-slate-100">
            <div className="flex flex-col items-center text-slate-400">
              <Upload className="h-4 w-4" />
              <span className="mt-0.5 text-[10px]">
                {(imgs.length > 0) ? "Add image" : "Upload"}
              </span>
            </div>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                handleImage(idx, f);
              }}
              data-testid={`item-image-${idx}`}
            />
          </label>
        </div>
      </div>
      <div className="col-span-12 md:col-span-5">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Description / Specs</Label>
        <Textarea
          value={li.description}
          onChange={(e) => setItem(idx, "description", e.target.value)}
          rows={4}
          placeholder="Heavy duty plastic pallet 1200x1000mm, dynamic load 1500kg…"
          className="mt-1 rounded-sm"
          data-testid={`item-desc-${idx}`}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Quantity</Label>
        <Input type="number" min={0} value={li.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="mt-1 rounded-sm tabular-nums" data-testid={`item-qty-${idx}`} />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Unit Price (AED)</Label>
        <Input type="number" min={0} step="0.01" value={li.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="mt-1 rounded-sm tabular-nums" data-testid={`item-price-${idx}`} />
      </div>
      <div className="col-span-4 md:col-span-1 flex flex-col">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Total</Label>
        <div className="mt-1 flex h-9 items-center justify-end pr-2 text-sm font-semibold tabular-nums ep-text-primary" data-testid={`item-total-${idx}`}>
          {lineTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
        <Button variant="ghost" size="sm" className="mt-auto rounded-sm text-red-600 hover:text-red-700" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Per-item Data Sheet */}
      <div className="col-span-12 mt-3 rounded-sm border border-[#E5E7EB] bg-slate-50/60 p-3">
        <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Data Sheet — Item {idx + 1}</div>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-12">
              <div className="md:col-span-5">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Model Number</Label>
                <div className="mt-1 flex gap-1">
                  <Input
                    list="quote-products-datalist"
                    value={modelValue}
                    onChange={(e) => handleModelChange(idx, e.target.value)}
                    onBlur={(e) => {
                      const p = productByModel(e.target.value);
                      if (p) applyProductToItem(idx, p);
                    }}
                    placeholder="Type or pick (e.g. EPBIN-PA315)"
                    className="rounded-sm bg-white"
                    data-testid={`item-model-${idx}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm whitespace-nowrap"
                    onClick={() => {
                      const p = productByModel(modelValue);
                      if (p) applyProductToItem(idx, p);
                      else toast.error("No data sheet saved for this model");
                    }}
                    data-testid={`apply-product-${idx}`}
                  >
                    Apply
                  </Button>
                </div>
              </div>
              <div className="md:col-span-7">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Category Template</Label>
                <select
                  value={li.category || ""}
                  onChange={(e) => applyCategory(idx, e.target.value)}
                  className="mt-1 w-full rounded-sm border border-[#E5E7EB] bg-white px-2 py-2 text-xs"
                  data-testid={`item-category-${idx}`}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm md:self-end" onClick={() => addItemSpec(idx)} data-testid={`add-item-spec-${idx}`}>
            <Plus className="mr-1 h-3 w-3" /> Add Row
          </Button>
        </div>

        {(li.specs || []).length === 0 ? (
          <div className="rounded-sm border border-dashed border-[#E5E7EB] bg-white p-3 text-center text-xs text-slate-400">
            No spec rows. Select a category above or add rows manually.
          </div>
        ) : (
          <div className="space-y-1.5">
            {(li.specs || []).map((sp, sIdx) => (
              <div key={sIdx} className="grid grid-cols-12 gap-2" data-testid={`item-${idx}-spec-${sIdx}`}>
                <div className="col-span-12 md:col-span-4">
                  <Input
                    value={sp.label}
                    onChange={(e) => setItemSpec(idx, sIdx, "label", e.target.value)}
                    placeholder="Label (e.g. Static Load)"
                    className="rounded-sm bg-white"
                    data-testid={`item-${idx}-spec-label-${sIdx}`}
                  />
                </div>
                <div className="col-span-10 md:col-span-6">
                  {sp.bullet ? (
                    <Textarea
                      value={sp.value}
                      onChange={(e) => setItemSpec(idx, sIdx, "value", e.target.value)}
                      rows={3}
                      placeholder={"One point per line, e.g.\nHeavy-duty construction\nSuitable for industrial use\nHigh load-bearing capacity"}
                      className="rounded-sm bg-white"
                      data-testid={`item-${idx}-spec-value-${sIdx}`}
                    />
                  ) : (
                    <Input
                      value={sp.value}
                      onChange={(e) => setItemSpec(idx, sIdx, "value", e.target.value)}
                      placeholder="Value (e.g. 5000 kg)"
                      className="rounded-sm bg-white"
                      data-testid={`item-${idx}-spec-value-${sIdx}`}
                    />
                  )}
                  {sp.bullet && (
                    <div className="mt-1 text-[10px] text-slate-400" data-testid={`item-${idx}-spec-bullet-hint-${sIdx}`}>
                      Bullet mode: each line becomes a separate bullet point.
                    </div>
                  )}
                </div>
                <div className="col-span-1 flex">
                  <Button
                    type="button"
                    variant={sp.bullet ? "default" : "outline"}
                    size="icon"
                    title={sp.bullet ? "Bullet point ON — click to turn off" : "Show this row as a bullet point"}
                    className={`h-9 w-9 rounded-sm ${sp.bullet ? "ep-amber hover:ep-amber" : ""}`}
                    onClick={() => setItemSpec(idx, sIdx, "bullet", !sp.bullet)}
                    data-testid={`item-${idx}-spec-bullet-${sIdx}`}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                <div className="col-span-1 flex">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-sm text-red-600 hover:text-red-700"
                    onClick={() => removeItemSpec(idx, sIdx)}
                    data-testid={`item-${idx}-remove-spec-${sIdx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});


const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const Row = ({ label, value, bold, testId }) => (
  <div className="flex items-center justify-between">
    <span className={bold ? "font-semibold ep-text-primary" : "text-slate-600"}>{label}</span>
    <span className={`tabular-nums ${bold ? "font-bold ep-text-primary text-base" : ""}`} data-testid={testId}>
      {Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
    </span>
  </div>
);

export default QuoteFormPage;
